import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, Alert, Spinner, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const DownloadPage = () => {
  const apiPrefix = window.location.pathname.match(/^\/[^\/]+/) ? window.location.pathname.match(/^\/[^\/]+/)[0].replace(/\/$/, '') : '';
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('jwt_token');
        if (!token) {
            navigate('/custom-login');
        }
    }, [navigate]);

    const handleLogout = () => {
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('user');
        navigate('/custom-login');
    };

    const handleSkip = async () => {
        try {
            await axios.post(apiPrefix + '/api/acknowledge-download-prompt');
            // Redirect to Overleaf dashboard
            window.location.href = '/project';
        } catch (err) {
            console.error('Failed to acknowledge prompt:', err);
            // Fallback redirect anyway so user isn't stuck
            window.location.href = '/project';
        }
    };

    const handleDownload = async () => {
        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            const token = localStorage.getItem('jwt_token'); // Assuming JWT token is stored in localStorage
            
            // Trigger background acknowledgement so they don't see the prompt again today
            axios.post(apiPrefix + '/api/acknowledge-download-prompt').catch(e => console.error('Ack failed:', e));

            const response = await axios.post(
                '/api/download-project',
                {}, 
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    responseType: 'blob', 
                }
            );

            // Create a Blob from the response data
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element to trigger the download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'overleaf_project_backup.zip'); // Suggest a filename
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);

            window.URL.revokeObjectURL(url); // Clean up the URL object

            setSuccess(true);
            console.log('Download initiated successfully.');
        } catch (err) {
            console.error('Download failed:', err);
            const errorMessage = err.response?.data?.error || err.message || 'An unknown error occurred during download.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="mt-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Download Your Overleaf Project & Data</h2>
                <Button variant="outline-danger" onClick={handleLogout}>Logout</Button>
            </div>
            <p>Click the button below to download a zip archive containing your project files and associated database data.</p>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && (
                <Alert variant="success">
                    Download started! Check your browser's download folder.
                    <div className="mt-2">
                        <Button variant="success" size="sm" onClick={() => window.location.href = '/project'}>
                            Continue to Overleaf
                        </Button>
                    </div>
                </Alert>
            )}

            <div className="d-flex gap-3">
                <Button
                    variant="primary"
                    onClick={handleDownload}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                            {' '} Downloading...
                        </>
                    ) : (
                        'Download My Project'
                    )}
                </Button>

                <Button
                    variant="secondary"
                    onClick={handleSkip}
                    disabled={loading}
                >
                    Skip and Continue to Overleaf
                </Button>
            </div>
        </Container>
    );
};

export default DownloadPage;
